import { useParams } from 'react-router-dom';

import formatDate from '../../../../../../../../../helpers/formatDate';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { Person } from '@/api/types/backend-dto-types';
import { Run } from '@/api/types/backend-dto-types';
import { transformFilterName } from '@/helpers/filterNames';
import { DateRangeFilter } from '@/new/components/date-range-filter';
import { DocumentTypeFilter } from '@/new/components/doc-type-filter';
import { DocumentFilter } from '@/new/components/document-filter';
import { FilterModalContent } from '@/new/components/filter-modal-content';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Checkbox } from '@/new/components/shadcn/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/new/components/shadcn/dialog';
import { Label } from '@/new/components/shadcn/label';
import {
  MultiSelect,
  MultiSelectContent,
  MultiSelectGroup,
  MultiSelectItem,
  MultiSelectTrigger,
  MultiSelectValue,
} from '@/new/components/shadcn/multi-select';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';
import { FactFilters as FactFiltersType, Relevance, Classification, MyOptionType } from '@/types';

interface FilterFactsDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  filterCount: number;
  activeFilters: FactFiltersType;
  allDocumentTypes: string[];
  setFilters: (v: FactFiltersType) => void;
  caseRuns: Run[];
  caseThemes: string[];
  allPersons: Person[];
  goToPage: (value: number) => void;
  totalUnfilteredCount?: number;
}

export const FilterFactsDialog = ({
  isOpen,
  onOpenChange,
  filterCount,
  activeFilters,
  allDocumentTypes,
  setFilters,
  caseRuns,
  caseThemes,
  allPersons,
  goToPage,
  totalUnfilteredCount,
}: FilterFactsDialogProps) => {
  const { matterId } = useParams();
  if (!matterId) return null;

  // Use maps for included/excluded docs for better performance
  const includedDocs = new Map(
    (activeFilters.docIds || []).filter((doc) => doc !== null).map((doc) => [doc.value, doc]),
  );
  const excludedDocs = new Map(
    (activeFilters.excludedDocIds || []).filter((doc) => doc !== null).map((doc) => [doc.value, doc]),
  );

  const applyFilters = (filters: FactFiltersType) => {
    setFilters(filters);
    goToPage(0);
  };

  const handleClear = () => {
    applyFilters({
      classification: [],
      docIds: [],
      excludedDocIds: [],
      relevance: [],
      runIds: [],
      keyFacts: false,
      disputedFacts: false,
      startDate: null,
      endDate: null,
      documentTypes: [],
      themes: [],
      personIds: [],
      searchQuery: activeFilters.searchQuery || '',
    });
    onOpenChange(false);
  };

  const handleDocsChange = ({
    includedDocs,
    excludedDocs,
  }: {
    includedDocs: Map<string, MyOptionType> | undefined;
    excludedDocs: Map<string, MyOptionType> | undefined;
  }) => {
    trackCustomEvent(TrackingEventsV2.FactFilterByDocument);
    applyFilters({
      ...activeFilters,
      docIds: Array.from(includedDocs?.values() || []),
      excludedDocIds: Array.from(excludedDocs?.values() || []),
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button>
          <Icon className="lucide--funnel-plus" />
          Add Filters
          {filterCount > 0 && (
            <span className="absolute -top-2 -right-2 flex size-4.5 items-center justify-center rounded-full border border-teal-400 bg-teal-50 pt-[1px] text-xs font-medium text-teal-600">
              {filterCount}
            </span>
          )}
        </Button>
      </DialogTrigger>

      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>
            <Icon className="lucide--filter" />
            Filter Facts
          </DialogTitle>
        </DialogHeader>

        <FilterModalContent onClear={handleClear}>
          {totalUnfilteredCount !== undefined && totalUnfilteredCount >= 2_000_000 && (
            <div className="flex gap-2.5 rounded-md border border-sky-300 bg-sky-50 px-4 py-3 text-sky-600">
              <Icon className="lucide--info mt-0.75" />
              <p>
                Filtering performance may be degraded when viewing over 2 million facts. Consider{' '}
                <a
                  href={`/matters/${matterId}/subsets/new`}
                  target="_blank"
                  className="font-medium duration-150 hover:text-sky-500"
                >
                  <span>creating a smaller subset</span>{' '}
                  <Icon className="lucide--external-link -mb-0.5 ml-0.25 inline-block" />
                </a>{' '}
                of documents.
              </p>
            </div>
          )}

          {/* Disputed Facts Filter */}
          <div className="flex items-center gap-8">
            <div className="flex items-center gap-2">
              <Icon className="lucide--alert-triangle h-3 w-3 text-rose-500 opacity-60" />
              <Label htmlFor="disputed-facts" className="m-0">
                Disputed facts
              </Label>

              <Checkbox
                id="disputed-facts"
                checked={Boolean(activeFilters.disputedFacts)}
                onCheckedChange={(checked) => {
                  trackCustomEvent(TrackingEventsV2.FactToggleDisputed);
                  applyFilters({ ...activeFilters, disputedFacts: Boolean(checked) });
                }}
              />
            </div>

            {/* Key Facts Filter */}
            <div className="flex items-center gap-2">
              <Icon className="lucide--star h-3 w-3 text-amber-500 opacity-60" />
              <Label htmlFor="key-facts" className="m-0">
                Key facts
              </Label>

              <Checkbox
                id="key-facts"
                checked={Boolean(activeFilters.keyFacts)}
                onCheckedChange={(checked) => {
                  trackCustomEvent(TrackingEventsV2.FactToggleKey);
                  applyFilters({ ...activeFilters, keyFacts: Boolean(checked) });
                }}
              />
            </div>
          </div>

          {/* Documents Filter */}
          <div>
            <Label className="mb-2 block">Documents</Label>
            <DocumentFilter
              includedDocs={includedDocs}
              excludedDocs={excludedDocs}
              handleDocsChange={handleDocsChange}
              mode="include-facts-from"
            />
          </div>

          {/* Document Type Filter */}
          <div>
            <Label className="mb-2 block">Document Type</Label>
            <DocumentTypeFilter
              selectedTypes={activeFilters.documentTypes || []}
              setSelectedTypes={(types) => {
                trackCustomEvent(TrackingEventsV2.FactFilterByDocumentType);
                applyFilters({ ...activeFilters, documentTypes: types });
              }}
              allDocumentTypes={allDocumentTypes}
            />
          </div>

          {/* Themes Filter */}
          <div>
            <Label className="mb-2 block">Issues</Label>

            <MultiSelect
              values={activeFilters.themes || []}
              onValuesChange={(values) => {
                trackCustomEvent(TrackingEventsV2.FactFilterByTheme);
                applyFilters({ ...activeFilters, themes: values });
              }}
            >
              <MultiSelectTrigger className="w-full">
                <MultiSelectValue placeholder="All issues" />
              </MultiSelectTrigger>
              <MultiSelectContent>
                <MultiSelectGroup>
                  {caseThemes.map((theme) => (
                    <MultiSelectItem key={theme} value={theme}>
                      {theme}
                    </MultiSelectItem>
                  ))}
                </MultiSelectGroup>
              </MultiSelectContent>
            </MultiSelect>
          </div>

          {/* Relevance and Classification in horizontal layout */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="mb-2 block">Relevance</Label>

              <MultiSelect
                values={activeFilters.relevance || []}
                onValuesChange={(values) => {
                  trackCustomEvent(TrackingEventsV2.FactFilterByRelevance);
                  applyFilters({ ...activeFilters, relevance: values.map((value) => value as Relevance) });
                }}
              >
                <MultiSelectTrigger className="w-full">
                  <MultiSelectValue placeholder="All relevance levels" />
                </MultiSelectTrigger>

                <MultiSelectContent>
                  <MultiSelectGroup>
                    {Object.values(Relevance).map((relevance) => (
                      <MultiSelectItem key={relevance} value={relevance}>
                        {transformFilterName(relevance, 'factRelevance')}
                      </MultiSelectItem>
                    ))}
                  </MultiSelectGroup>
                </MultiSelectContent>
              </MultiSelect>
            </div>
            <div>
              <Label className="mb-2 block">Classification</Label>
              <MultiSelect
                values={activeFilters.classification || []}
                onValuesChange={(values) => {
                  trackCustomEvent(TrackingEventsV2.FactFilterByClassification);
                  applyFilters({ ...activeFilters, classification: values.map((value) => value as Classification) });
                }}
              >
                <MultiSelectTrigger className="w-full">
                  <MultiSelectValue placeholder="All classifications" />
                </MultiSelectTrigger>
                <MultiSelectContent>
                  <MultiSelectGroup>
                    {Object.values(Classification).map((classification) => (
                      <MultiSelectItem key={classification} value={classification}>
                        {transformFilterName(classification)}
                      </MultiSelectItem>
                    ))}
                  </MultiSelectGroup>
                </MultiSelectContent>
              </MultiSelect>
            </div>
          </div>

          {/* Processing Run and Entities in horizontal layout */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="mb-2 block">Processing Run</Label>

              <MultiSelect
                values={(activeFilters.runIds || []).filter((value): value is string => value !== undefined)}
                onValuesChange={(values) => {
                  trackCustomEvent(TrackingEventsV2.FactFilterByProcessingRun);
                  applyFilters({ ...activeFilters, runIds: values });
                }}
              >
                <MultiSelectTrigger className="w-full">
                  <MultiSelectValue placeholder="All processing runs" />
                </MultiSelectTrigger>
                <MultiSelectContent>
                  <MultiSelectGroup>
                    {caseRuns.map((run) => (
                      <Tooltip key={run.id}>
                        <TooltipTrigger className="w-full">
                          <MultiSelectItem value={run.id}>
                            <div className="flex w-full items-center justify-between gap-4">
                              {run.name ? (
                                <span className="max-w-52 truncate">{run.name}</span>
                              ) : (
                                <span className="text-gray-500">Unnamed Run</span>
                              )}

                              <span className="text-xs text-gray-500">{formatDate(run.createdDate, true, true)}</span>
                            </div>
                          </MultiSelectItem>
                        </TooltipTrigger>
                        <TooltipContent side="right">
                          {run.name ? (
                            <p className="text-xs font-medium">{run.name}</p>
                          ) : (
                            <p className="text-xs text-gray-500">Unnamed Run</p>
                          )}
                          <p className="text-xs text-gray-500">{formatDate(run.createdDate, true, true)}</p>
                        </TooltipContent>
                      </Tooltip>
                    ))}
                  </MultiSelectGroup>
                </MultiSelectContent>
              </MultiSelect>
            </div>
            <div>
              <Label className="mb-2 block">Entities</Label>

              <MultiSelect
                values={activeFilters.personIds || []}
                onValuesChange={(values) => {
                  trackCustomEvent(TrackingEventsV2.FactFilterByPerson);
                  applyFilters({ ...activeFilters, personIds: values });
                }}
              >
                <MultiSelectTrigger className="w-full">
                  <MultiSelectValue placeholder="All entities" />
                </MultiSelectTrigger>
                <MultiSelectContent>
                  <MultiSelectGroup>
                    {allPersons.map((person) => (
                      <MultiSelectItem key={person.id} value={person.id}>
                        {person.name}
                      </MultiSelectItem>
                    ))}
                  </MultiSelectGroup>
                </MultiSelectContent>
              </MultiSelect>
            </div>
          </div>

          {/* Fact Date Range Filter */}
          <div>
            <Label className="mb-2 block">Fact Date Range</Label>
            <DateRangeFilter
              startDate={activeFilters.startDate ? new Date(activeFilters.startDate) : null}
              endDate={activeFilters.endDate ? new Date(activeFilters.endDate) : null}
              setStartDate={(date) => applyFilters({ ...activeFilters, startDate: date?.toISOString() || null })}
              setEndDate={(date) => applyFilters({ ...activeFilters, endDate: date?.toISOString() || null })}
            />
          </div>
        </FilterModalContent>
      </DialogContent>
    </Dialog>
  );
};
